import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';
import AuthGuard from './components/AuthGuard';
import AppLayout from './components/layout/AppLayout';
import Spinner from './components/ui/Spinner';
import useAuthStore from './store/authStore';
import { InteractionStatus, useSafeMsal } from './authRuntime';
import { BenchAlertProvider } from './components/bench/BenchAlertProvider';

// Lazy-loaded pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const BenchPool = lazy(() => import('./components/BenchPool'));
const Pipeline = lazy(() => import('./components/Pipeline'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const EmployeeProfile = lazy(() => import('./pages/EmployeeProfile'));
const SkillMatch = lazy(() => import('./pages/SkillMatch'));
const InterviewForm = lazy(() => import('./pages/InterviewForm'));
const PmFeedback = lazy(() => import('./pages/PmFeedback'));
const PatternFlagsPage = lazy(() => import('./pages/PatternFlagsPage'));
const IndentPage = lazy(() => import('./pages/IndentPage'));
const SelfServicePortal = lazy(() => import('./pages/SelfServicePortal'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const OnProjectAllocation = lazy(() => import('./pages/OnProjectAllocation'));
const TrainingForm = lazy(() => import('./pages/TrainingForm'));
const TrainingList = lazy(() => import('./pages/TrainingList'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const SubStatusPage = lazy(() => import('./pages/SubStatusPage'));
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const IdtApplicationsPage = lazy(() => import('./pages/IdtApplicationsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AllocationHistoryPage = lazy(() => import('./pages/AllocationHistoryPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const BenchMovementQueue = lazy(() => import('./pages/bench/BenchMovementQueue'));

// Placeholder pages
function ComingSoon({ title }) {
  return (
    <div className="coming-soon" role="main" aria-label={`${title} - coming soon`}>
      {title} — Coming Soon
    </div>
  );
}

function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <Spinner size="lg" />
    </div>
  );
}

function DefaultRedirect() {
  const { user, isAuthenticated } = useAuthStore();
  const { inProgress } = useSafeMsal();

  // While MSAL is processing a redirect, don't navigate yet
  if (inProgress !== InteractionStatus.None) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const redirectPath = user?.roles?.some(r =>
    ['Super Admin', 'Ops Admin', 'RMG Lead', 'PMO Team', 'DU Head', 'HR', 'PM', 'Finance', 'Training Team'].includes(r)
  ) ? '/dashboard' : '/portal';
  return <Navigate to={redirectPath} replace />;
}

function App() {
  return (
    <Router>
      <BenchAlertProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'var(--c-surface-2)',
              color: 'var(--c-text-1)',
              border: '1px solid var(--c-border-2)',
              fontFamily: 'var(--font)',
              fontSize: '13px',
              borderRadius: '10px',
              boxShadow: 'var(--shadow-lg)',
            },
          }}
        />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected — wrapped in AppLayout */}
            <Route
              path="/"
              element={
                <AuthGuard>
                  <AppLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route index element={<DefaultRedirect />} />
                      </Routes>
                    </Suspense>
                  </AppLayout>
                </AuthGuard>
              }
            />

            <Route
              path="/dashboard"
              element={
                <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'RMG Lead', 'PMO Team', 'DU Head', 'HR', 'Project Manager', 'Finance', 'Training Team', 'Admin', 'PMO Lead', 'PMO Executive', 'PM', 'TTD Executive']}>
                  <AppLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Dashboard />
                    </Suspense>
                  </AppLayout>
                </AuthGuard>
              }
            />

            <Route
              path="/bench"
              element={
                <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'RMG Lead', 'PMO Team', 'DU Head']}>
                  <AppLayout>
                    <Suspense fallback={<PageLoader />}>
                      <BenchPool />
                    </Suspense>
                  </AppLayout>
                </AuthGuard>
              }
            />

            <Route
              path="/rmg/bench-movements"
              element={
                <AuthGuard requiredRoles={['Super Admin', 'RMG_APPROVER', 'RMG TEAM', 'PMO LEAD']}>
                  <AppLayout>
                    <Suspense fallback={<PageLoader />}>
                      <BenchMovementQueue />
                    </Suspense>
                  </AppLayout>
                </AuthGuard>
              }
            />

          <Route
            path="/pipeline"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'PMO Team', 'RMG Lead', 'DU Head', 'PM']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <Pipeline />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/interviews"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'PMO Team', 'RMG Lead', 'DU Head', 'PM']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <InterviewForm />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          {/* Projects LIST page */}
          <Route
            path="/customers"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'RMG Lead', 'PMO Team', 'Finance', 'DU Head', 'Project Manager']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <CustomersPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/projects"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'RMG Lead', 'PMO Team', 'Finance']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <ProjectsPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          {/* Project DETAIL page */}
          <Route
            path="/projects/:id"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'RMG Lead', 'PMO Team', 'Finance']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <ProjectDetail />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          {/* Project ALLOCATION page */}
          <Route
            path="/projects/:id/allocation"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'RMG Lead', 'PMO Team', 'Finance', 'PM']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <OnProjectAllocation />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/skill-match"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'PMO Team', 'RMG Lead']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <SkillMatch />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/employees/:id"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'RMG Lead', 'DU Head', 'HR', 'PMO Team', 'Training Team']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <EmployeeProfile />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/employees"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'RMG Lead', 'DU Head', 'HR', 'PMO Team', 'Training Team']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <EmployeesPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/interviews/new"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'PMO Team', 'RMG Lead', 'PM']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <InterviewForm />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          {/* Training LIST — all operational roles */}
          <Route
            path="/training"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'PMO Team', 'RMG Lead', 'DU Head', 'HR', 'Training Team', 'Employee']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <TrainingList />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          {/* Training NEW — all operational roles */}
          <Route
            path="/training/new"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'PMO Team', 'RMG Lead', 'DU Head', 'HR', 'Training Team', 'Employee']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <TrainingList />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          {/* Training FORM via /training/form — all operational roles */}
          <Route
            path="/training/form"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'PMO Team', 'RMG Lead', 'DU Head', 'HR', 'Training Team', 'Employee']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <TrainingList />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/idt-applications"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'PMO Team', 'RMG Lead', 'DU Head']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <IdtApplicationsPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/pm-feedback"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'PM', 'PMO Team', 'RMG Lead', 'DU Head', 'HR']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <PmFeedback />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/reports"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'RMG Lead', 'PMO Team', 'DU Head', 'HR', 'Project Manager', 'Finance', 'Training Team', 'Admin', 'PMO Lead', 'PMO Executive', 'PM', 'TTD Executive']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <ReportsPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/invoicing"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'Finance']}>
                <AppLayout>
                  <ComingSoon title="Invoicing" />
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/pattern-flags"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'PMO Team', 'RMG Lead', 'HR']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <PatternFlagsPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/indents"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Ops Admin', 'PMO Team', 'RMG Lead', 'DU Head', 'PM']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <IndentPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/notifications"
            element={
              <AuthGuard>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <NotificationsPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/admin/users"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Admin', 'Ops Admin', 'RMG Lead', 'RMG Team', 'PMO Team', 'PMO Lead']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <UserManagement />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/admin/sub-status"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Admin', 'Ops Admin', 'RMG Lead', 'RMG Team', 'PMO Team', 'PMO Lead']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <SubStatusPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/admin/resumes"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Admin', 'Ops Admin', 'PMO Team', 'PMO Lead', 'PMO Executive', 'RMG Lead', 'RMG Team', 'RMG_APPROVER']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <AdminPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/admin/allocation-history"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Admin', 'Ops Admin', 'RMG Lead', 'RMG Team', 'RMG_APPROVER', 'PMO Team', 'PMO Lead', 'PMO Executive']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <AllocationHistoryPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/admin"
            element={
              <AuthGuard requiredRoles={['Super Admin', 'Admin', 'Ops Admin', 'PMO Team', 'PMO Lead', 'PMO Executive', 'RMG Lead', 'RMG Team', 'RMG_APPROVER']}>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <AdminPage />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route
            path="/portal"
            element={
              <AuthGuard>
                <AppLayout>
                  <Suspense fallback={<PageLoader />}>
                    <SelfServicePortal />
                  </Suspense>
                </AppLayout>
              </AuthGuard>
            }
          />

          <Route path="/" element={<DefaultRedirect />} />

          {/* 404 Catch-all */}
          <Route
            path="*"
            element={
              <AppLayout>
                <Suspense fallback={<PageLoader />}>
                  <NotFoundPage />
                </Suspense>
              </AppLayout>
            }
          />
        </Routes>
      </Suspense>
      </BenchAlertProvider>
    </Router>
  );
}

export default App;
